import { useState, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const ALL_NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Shop', to: '/shop', shopOnly: true },
  { label: 'Track Order', to: '/track', shopOnly: true },
  { label: 'Services', to: '/services' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Contact', to: '/contact' },
];

const LOCAL_PAGES = [
  { label: 'Web Design Bournemouth', to: '/web-design-bournemouth' },
  { label: 'Barber Websites', to: '/web-design-barbers-bournemouth' },
  { label: 'Gym Websites', to: '/web-design-gyms-bournemouth' },
  { label: 'Restaurant Websites', to: '/web-design-restaurants-bournemouth' },
  { label: 'Trades & Construction', to: '/web-design-trades-bournemouth' },
];

export default function Footer() {
  const [shopVisible, setShopVisible] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/settings`)
      .then(r => r.json())
      .then(d => { if (d.shop_visible === false) setShopVisible(false); })
      .catch(() => {});
  }, []);

  const navLinks = shopVisible ? ALL_NAV_LINKS : ALL_NAV_LINKS.filter(l => !l.shopOnly);
  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#00E5FF]">
                <span className="font-['Outfit'] font-black text-[#0A0A0A] text-sm leading-none">Ui</span>
              </div>
              <span className="font-['Outfit'] font-bold text-lg text-white tracking-tight">
                UI DESIGNS
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-slate-500 max-w-xs mb-3">
              Affordable web design, hosting, and ongoing care for small businesses across Bournemouth, Poole, and Christchurch.
            </p>
            <p className="text-xs text-slate-600 italic">You & I, designing together.</p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <h4 className="font-['Outfit'] text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-6">
              Pages
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Local Pages */}
          <div className="md:col-span-3">
            <h4 className="font-['Outfit'] text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-6">
              Local Services
            </h4>
            <ul className="space-y-3">
              {LOCAL_PAGES.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="font-['Outfit'] text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-6">
              Contact
            </h4>
            <a
              data-testid="footer-email"
              href="mailto:info@uidesigns.co.uk"
              className="text-sm text-slate-400 hover:text-[#00E5FF] transition-colors block mb-3"
            >
              info@uidesigns.co.uk
            </a>
            <a
              data-testid="footer-phone"
              href="tel:+447466981840"
              className="text-sm text-slate-400 hover:text-[#00E5FF] transition-colors block mb-6"
            >
              +44 7466 981840
            </a>
            <button
              data-testid="footer-back-to-top"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-white transition-colors"
            >
              Back to top
              <ArrowUp className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Service Areas */}
        <div data-testid="footer-service-areas" className="mt-12 pt-8 border-t border-white/5">
          <h4 className="font-['Outfit'] text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
            Service Areas
          </h4>
          <p className="text-xs text-slate-600 leading-relaxed">
            Bournemouth (BH1–BH11) &middot; Poole (BH12–BH18) &middot; Christchurch (BH23) &middot; and across Dorset
          </p>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-slate-600">
            &copy; {new Date().getFullYear()} UI DESIGNS. All rights reserved.
          </p>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link
              to="/ai-sitemap"
              data-testid="footer-ai-sitemap-link"
              className="text-xs text-slate-500 hover:text-[#00E5FF] transition-colors"
            >
              Sitemap
            </Link>
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className="text-xs text-slate-500 hover:text-[#00E5FF] transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              data-testid="footer-terms-link"
              className="text-xs text-slate-500 hover:text-[#00E5FF] transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
